import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Fade,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '../../react-query';
import FirmwideSummaryRiskSection from './components/FirmwideSummaryRiskSection';
import StrategyRiskSection from './components/StrategyRiskSection';
import StrategyRiskTrendsPanel from './components/StrategyRiskTrendsPanel';
import RiskFrameworkSkeleton from './components/RiskFrameworkSkeleton';
import { FIRMWIDE } from './components/StrategyRiskAnalyticsDialog';
import { countOverLimit, getAttentionItems } from './utils/limitStatus';
import RiskLimitsDatePicker from '../risk-limits/components/RiskLimitsDatePicker';
import { useFirmwideSummaryData } from './hooks/useFirmwideSummaryData';
import {
  useLatestValuationDate,
  useAvailableValuationDates,
  useRiskLimitsThresholds,
} from '../risk-limits/hooks/useRiskLimitsData';

export default function FirmwideSummaryPage() {
  const queryClient = useQueryClient();
  const latestDate = useLatestValuationDate();
  const availableDates = useAvailableValuationDates();
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('millions');
  const [trendScope, setTrendScope] = useState(FIRMWIDE);
  const trendsRef = useRef(null);
  // Card density is a personal preference, remembered across visits.
  const [density, setDensity] = useState(() => {
    try {
      return localStorage.getItem('riskFramework.density') === 'compact' ? 'compact' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const handleDensityChange = (next) => {
    setDensity(next);
    try {
      localStorage.setItem('riskFramework.density', next);
    } catch {
      /* storage unavailable: keep the choice for this visit only */
    }
  };

  const {
    firmwide,
    strategies,
    portfolioGreeks,
    isLoading,
    isFetching,
    varLoading,
    isError,
    error,
    asOfDate,
  } = useFirmwideSummaryData(selectedDate);

  // The limits in force on the date being viewed (limits are stored by date), not today's.
  const { data: thresholds = [] } = useRiskLimitsThresholds(selectedDate || latestDate);
  const attentionItems = useMemo(() => getAttentionItems(strategies, thresholds), [strategies, thresholds]);

  const scrollToTrends = () => trendsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleSelectTrend = (name) => {
    setTrendScope(name);
    scrollToTrends();
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries(['firmwideSummary']);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: '700', mb: 1 }}>
            Risk Framework
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Firmwide risk metrics and strategy-level performance across all portfolios
            {asOfDate ? ` as of ${asOfDate}` : ''}.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newViewMode) => {
              if (newViewMode !== null) {
                setViewMode(newViewMode);
              }
            }}
            size="small"
            sx={{
              height: 'fit-content',
            }}
          >
            <ToggleButton value="millions" aria-label="millions view">
              <Typography variant="caption" sx={{ fontSize: '12px' }}>
                $ Millions
              </Typography>
            </ToggleButton>
            <ToggleButton value="percentage" aria-label="percentage of nav view">
              <Typography variant="caption" sx={{ fontSize: '12px' }}>
                % of AUM
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          <RiskLimitsDatePicker
            value={selectedDate}
            latestDate={latestDate}
            availableDates={availableDates}
            onChange={setSelectedDate}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {isError && (
        <Alert severity="error">
          {error?.message || 'Failed to load firmwide summary data.'}
        </Alert>
      )}

      {/* Loading State: skeleton mirrors the layout; content fades in once data lands */}
      {isLoading ? (
        <RiskFrameworkSkeleton />
      ) : (
        <Fade in appear timeout={400}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* 1. Status strip: firmwide snapshot + limit status */}
          <FirmwideSummaryRiskSection
            firmwideData={firmwide}
            viewMode={viewMode}
            overLimitCount={countOverLimit(strategies, thresholds)}
            strategyCount={strategies.length}
            onViewTrends={scrollToTrends}
            varLoading={varLoading}
          />

          {/* 2. Limits & positions by strategy; the chart icon on a card focuses it in Risk Trends */}
          <StrategyRiskSection density={density} onDensityChange={handleDensityChange} attentionItems={attentionItems} strategies={strategies} asOfDate={asOfDate} viewMode="threshold" thresholds={thresholds} aum={firmwide?.aum} displayMode={viewMode} portfolioGreeks={portfolioGreeks} onSelectTrend={handleSelectTrend} />

          {/* 3. Trends: how we got here */}
          <Box ref={trendsRef} sx={{ scrollMarginTop: 16 }}>
            <StrategyRiskTrendsPanel asOfDate={asOfDate} selected={trendScope} onSelect={setTrendScope} />
          </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
